import { LogOut, MonitorSmartphone } from 'lucide-react';
import { useState } from 'react';
import { logout, logoutEverywhere } from '../../api/auth';
import { useToast } from '../../components/toast';
import { Button, ConfirmSheet } from '../../components/ui';
import { useT } from '../../i18n';
import { PendingChangesError } from '../../sync/pending';
import { SettingsSection } from './parts';

type Mode = 'here' | 'all';

/**
 * Sign out here / everywhere. Logout first tries to send queued changes (calendar writes,
 * annotations); when some cannot be sent, a second sheet asks "N óvistaðar breytingar – skrá út samt?".
 */
export function AccountSection() {
  const t = useT();
  const toast = useToast();
  const [confirm, setConfirm] = useState<Mode | null>(null);
  const [pending, setPending] = useState<{ mode: Mode; count: number } | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (mode: Mode, force: boolean) => {
    setBusy(true);
    try {
      if (mode === 'all') await logoutEverywhere({ force });
      else await logout({ force });
      // On success the auth layer clears ['me'] and the app shows the login screen.
    } catch (e) {
      setBusy(false);
      setConfirm(null);
      if (e instanceof PendingChangesError) {
        setPending({ mode, count: e.count });
        return;
      }
      setPending(null);
      toast.show(t('settings.logout_failed'), { tone: 'error' });
    }
  };

  return (
    <SettingsSection icon={LogOut} title={t('settings.account')} id="settings-account">
      <div className="flex flex-col gap-3 p-5 sm:flex-row">
        <Button variant="secondary" icon={LogOut} block onClick={() => setConfirm('here')}>
          {t('settings.logout')}
        </Button>
        <Button variant="secondary" icon={MonitorSmartphone} block className="text-[var(--danger-text)]" onClick={() => setConfirm('all')}>
          {t('settings.logout_all')}
        </Button>
      </div>
      <ConfirmSheet
        open={confirm !== null}
        title={confirm === 'all' ? t('settings.logout_all_confirm') : t('settings.logout_confirm')}
        body={confirm === 'all' ? t('settings.logout_all_body') : t('settings.logout_body')}
        confirmLabel={confirm === 'all' ? t('settings.logout_all') : t('settings.logout')}
        cancelLabel={t('common.cancel')}
        danger={confirm === 'all'}
        busy={busy}
        onConfirm={() => confirm && void run(confirm, false)}
        onClose={() => !busy && setConfirm(null)}
      />
      <ConfirmSheet
        open={pending !== null}
        title={t('offline.logout_pending_title', { count: pending?.count ?? 0 })}
        body={t('offline.logout_pending_body')}
        confirmLabel={t('offline.logout_anyway')}
        cancelLabel={t('common.cancel')}
        danger
        busy={busy}
        onConfirm={() => pending && void run(pending.mode, true)}
        onClose={() => !busy && setPending(null)}
      />
    </SettingsSection>
  );
}
